import './styles/main.css';
import Phaser from 'phaser';
import { gameState, STAT_DEFINITIONS } from './services/gameState.js';
import { sound } from './services/audioService.js';
import { ArcGauge } from './ui/arcGauge.js';
import { StatsView } from './ui/statsView.js';
import { TimeWarpToolbar } from './ui/timeWarpToolbar.js';

// Scenes
import { BootScene } from './scenes/BootScene.js';
import { MainScene } from './scenes/MainScene.js';
import { FoodGame } from './scenes/minigames/FoodGame.js';
import { FunGame } from './scenes/minigames/FunGame.js';
import { WorkGame } from './scenes/minigames/WorkGame.js';
import { SleepGame } from './scenes/minigames/SleepGame.js';
import { SocialGame } from './scenes/minigames/SocialGame.js';
import { FitnessGame } from './scenes/minigames/FitnessGame.js';

class AppController {
  constructor() {
    this.petGame = null;
    this.activeMiniGame = null;
    this.selectedStatId = null;
    this.init();
  }

  init() {
    // 1. Initialize UI Components
    const gaugeCanvas = document.getElementById('arc-gauge-canvas');
    const percentText = document.getElementById('gauge-percentage-text');
    const moodBadge = document.getElementById('gauge-mood-badge');
    this.arcGauge = new ArcGauge(gaugeCanvas, percentText, moodBadge);

    const statsContainer = document.getElementById('stats-list-container');
    this.statsView = new StatsView(statsContainer, (statId) => {
      this.promptDifficulty(statId);
    });

    const timeWarpContainer = document.getElementById('timewarp-modal');
    const toastEl = document.getElementById('game-toast-el');
    this.timeWarp = new TimeWarpToolbar(timeWarpContainer, toastEl);

    // 2. Setup Header Actions
    this.setupHeaderActions();

    // 3. Initialize Pet Stage Phaser Game
    this.initPetPhaser();

    // 4. Setup Modals
    this.setupModals();

    // 5. Subscribe to State Changes
    gameState.subscribe((state) => {
      this.arcGauge.update(state);
      this.statsView.render(state);
    });

    // 6. Register PWA Service Worker
    this.registerPWA();
  }

  setupHeaderActions() {
    const muteBtn = document.getElementById('mute-toggle-btn');
    muteBtn.addEventListener('click', () => {
      const isMuted = sound.toggleMute();
      muteBtn.textContent = isMuted ? '🔇' : '🔊';
    });

    const warpBtn = document.getElementById('open-timewarp-btn');
    warpBtn.addEventListener('click', () => {
      sound.playClick();
      this.timeWarp.open();
    });
  }

  initPetPhaser() {
    const petContainer = document.getElementById('phaser-pet-container');
    const config = {
      type: Phaser.AUTO,
      parent: petContainer,
      width: 320,
      height: 180,
      transparent: true,
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      scene: [MainScene]
    };

    this.petGame = new Phaser.Game(config);
  }

  setupModals() {
    // Difficulty modal
    const diffModal = document.getElementById('difficulty-modal');
    const closeDiffBtn = document.getElementById('close-diff-modal-btn');
    closeDiffBtn.addEventListener('click', () => {
      sound.playClick();
      diffModal.classList.remove('active');
    });

    const diffButtons = diffModal.querySelectorAll('[data-diff]');
    diffButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        sound.playClick();
        const difficulty = btn.getAttribute('data-diff');
        diffModal.classList.remove('active');
        if (this.selectedStatId) {
          this.startMiniGame(this.selectedStatId, difficulty);
        }
      });
    });

    // Mini-game Exit Button
    const exitGameBtn = document.getElementById('exit-minigame-btn');
    exitGameBtn.addEventListener('click', () => {
      sound.playClick();
      this.closeMiniGame();
    });
  }

  promptDifficulty(statId) {
    const def = STAT_DEFINITIONS[statId];
    if (!def) return;

    this.selectedStatId = statId;
    const diffModal = document.getElementById('difficulty-modal');
    document.getElementById('diff-modal-icon').textContent = def.icon;
    document.getElementById('diff-modal-title').textContent = `${def.gameTitle}`;
    document.getElementById('diff-modal-desc').textContent = def.gameSubtitle;
    diffModal.classList.add('active');
  }

  startMiniGame(statId, difficulty) {
    const def = STAT_DEFINITIONS[statId];
    if (!def) return;

    const barsToAdd = difficulty === 'hard' ? 9 : (difficulty === 'medium' ? 6 : 3);

    // Show mini-game screen overlay
    const modal = document.getElementById('minigame-screen-modal');
    document.getElementById('game-active-icon').textContent = def.icon;
    document.getElementById('game-active-title').textContent = def.gameTitle;
    document.getElementById('game-active-diff').textContent = `${difficulty.toUpperCase()} • Win +${barsToAdd} Bars`;
    modal.classList.add('active');

    // Destroy existing mini-game instance if any
    if (this.activeMiniGame) {
      this.activeMiniGame.destroy(true);
      this.activeMiniGame = null;
    }

    const container = document.getElementById('phaser-game-container');
    container.innerHTML = '';

    const rect = container.getBoundingClientRect();
    const gameWidth = Math.floor(rect.width || 360);
    const gameHeight = Math.floor(rect.height || 620);

    // Map stat to Scene
    const sceneMap = {
      food: FoodGame,
      fun: FunGame,
      work: WorkGame,
      sleep: SleepGame,
      social: SocialGame,
      fitness: FitnessGame
    };

    const targetSceneClass = sceneMap[statId] || FoodGame;

    const gameConfig = {
      type: Phaser.AUTO,
      parent: container,
      width: gameWidth,
      height: gameHeight,
      transparent: false,
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 0 },
          debug: false
        }
      },
      scene: [targetSceneClass]
    };

    this.activeMiniGame = new Phaser.Game(gameConfig);

    // Pass init data to the active scene once ready
    this.activeMiniGame.events.once('ready', () => {
      this.activeMiniGame.scene.start(targetSceneClass.name, {
        difficulty,
        barsToAdd,
        onComplete: (isWin) => {
          if (isWin) {
            gameState.rewardStat(statId, barsToAdd);
            this.timeWarp.showToast(`🎉 +${barsToAdd} Bars added to ${def.name}!`);
          }
          this.closeMiniGame();
        }
      });
    });
  }

  closeMiniGame() {
    const modal = document.getElementById('minigame-screen-modal');
    modal.classList.remove('active');

    if (this.activeMiniGame) {
      this.activeMiniGame.destroy(true);
      this.activeMiniGame = null;
    }
  }

  registerPWA() {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('./sw.js').catch((err) => {
          console.warn('ServiceWorker registration failed', err);
        });
      });
    }
  }
}

// Ensure robust boot on both DOM ready and deferred script execution
function bootApp() {
  if (!window.app) {
    window.app = new AppController();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootApp);
} else {
  bootApp();
}
